import { Button, Card, CardContent, CardHeader, CardTitle } from '@repo/shadcn';
import { useRef } from 'react';
import { toast } from 'sonner';

import { IwForm, IwFormRef } from '@/components/form';
import { TextAreaField, TextField } from '@/components/form/fields';
import {
    useGetStoreSettingsQuery,
    useUpdateStoreSettingsMutation,
} from '@/features/slice/settings';

import { toastError } from '../../../utils';
import { InvoiceSettingsSchema, InvoiceSettingsSchemaType } from '../schema';

export const InvoiceSettings = () => {
    const {
        data,
        isLoading: isLoadingSettings,
        isError: isErrorSettings,
    } = useGetStoreSettingsQuery({});
    const [
        updateStoreSettings,
        { isLoading: isUpdating, isError: _isUpdateError },
    ] = useUpdateStoreSettingsMutation();

    const formRef = useRef<IwFormRef<InvoiceSettingsSchemaType>>(null);
    const submitRef = useRef<HTMLButtonElement>(null);

    const handleSubmit = async (data: InvoiceSettingsSchemaType) => {
        try {
            await updateStoreSettings(data).unwrap();
            toast.success('Invoice settings updated successfully');
        } catch (error) {
            toastError(
                error,
                'InvoiceSettings:handleSubmit Failed to update invoice settings',
            );
        }
    };

    if (isLoadingSettings) {
        return <div>Loading...</div>;
    }

    if (isErrorSettings || !data) {
        return <div>Error loading settings</div>;
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <CardTitle>Invoice Settings</CardTitle>
                </CardHeader>
                <CardContent>
                    <IwForm
                        schema={InvoiceSettingsSchema}
                        ref={formRef}
                        logErrors
                        onSubmit={(data) => {
                            handleSubmit(data as InvoiceSettingsSchemaType);
                        }}
                        initialValues={data.data}
                    >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                            <TextField<InvoiceSettingsSchemaType>
                                name="store_name"
                                label="Store Name"
                            />

                            <TextField<InvoiceSettingsSchemaType>
                                name="store_address"
                                label="Store Address"
                            />

                            <TextField<InvoiceSettingsSchemaType>
                                name="store_phone_number"
                                label="Store Phone Number"
                            />

                            <TextField<InvoiceSettingsSchemaType>
                                name="footer_thank_you_message"
                                label="Footer Thank You Message"
                            />

                            <TextAreaField<InvoiceSettingsSchemaType>
                                name="footer_terms_and_conditions"
                                label="Footer Terms and Conditions"
                            />
                        </div>

                        <div className="flex justify-end">
                            <Button
                                disabled={
                                    formRef.current?.formState.isSubmitting ||
                                    isUpdating
                                }
                                type="submit"
                                ref={submitRef}
                                className="mt-4"
                                variant={'default'}
                            >
                                Submit
                            </Button>
                        </div>
                    </IwForm>
                </CardContent>
            </Card>
        </>
    );
};
